/**
 * RShield -> DocumentProtection bridge.
 *
 * The sender configures RShield per document on the Send / Generate tab
 * (RDocsWebApp -> Shared/SendCreate/RShield). Those values are persisted on the
 * document options (RDocs.Model DocumentSettings.RShieldOptionsDto) and are
 * surfaced to the reader on the /docs/rpddata response as:
 *
 *   {
 *     rShield: true,
 *     rShieldOptions: {
 *       focusProtection: true,
 *       resumeOnFullScreen: false,
 *       focusLossLimit: false,
 *       focusLossLimitValue: 1,
 *       banReader: false,
 *       notifySender: true
 *     }
 *   }
 *
 * This module converts that payload into the flag shape the existing
 * DocumentProtection hooks already understand, so nothing downstream needs to
 * know about RShield naming.
 *
 * Precedence:
 *   1. Document-level RShield (sender configured it for THIS document)  -> wins
 *   2. AppContext.DocumentProtection per-reader config (legacy opt-in)  -> fallback
 *   3. Nothing                                                         -> inert
 *
 * The rpddata response may not carry the RShield fields yet (older API build).
 * In that case `parseRShieldPayload` returns null and the reader falls back to
 * the existing behavior, unchanged.
 */

export const RSHIELD_FOCUS_LOSS_LIMIT_MIN = 1;
export const RSHIELD_FOCUS_LOSS_LIMIT_MAX = 30;

export const RSHIELD_DEFAULT_OPTIONS = {
  focusProtection: true,
  resumeOnFullScreen: false,
  focusLossLimit: false,
  focusLossLimitValue: RSHIELD_FOCUS_LOSS_LIMIT_MIN,
  banReader: false,
  notifySender: true,
};

const toBool = (value, fallback = false) => {
  if (value === true || value === "true" || value === 1 || value === "1") return true;
  if (value === false || value === "false" || value === 0 || value === "0") return false;
  return fallback;
};

const clampLimit = (value) => {
  const parsed = parseInt(value, 10);
  if (isNaN(parsed)) return RSHIELD_FOCUS_LOSS_LIMIT_MIN;
  return Math.min(
    Math.max(parsed, RSHIELD_FOCUS_LOSS_LIMIT_MIN),
    RSHIELD_FOCUS_LOSS_LIMIT_MAX
  );
};

/**
 * Mirrors RShieldOptionsDto.Normalize() on the API so the reader can never act
 * on an impossible combination, whatever the stored document holds.
 */
export function normalizeRShieldOptions(raw) {
  const src = raw && typeof raw === "object" ? raw : {};

  const options = {
    focusProtection: toBool(src.focusProtection, RSHIELD_DEFAULT_OPTIONS.focusProtection),
    resumeOnFullScreen: toBool(src.resumeOnFullScreen, RSHIELD_DEFAULT_OPTIONS.resumeOnFullScreen),
    focusLossLimit: toBool(src.focusLossLimit, RSHIELD_DEFAULT_OPTIONS.focusLossLimit),
    focusLossLimitValue: clampLimit(src.focusLossLimitValue),
    banReader: toBool(src.banReader, RSHIELD_DEFAULT_OPTIONS.banReader),
    notifySender: toBool(src.notifySender, RSHIELD_DEFAULT_OPTIONS.notifySender),
  };

  if (!options.focusProtection) {
    options.resumeOnFullScreen = false;
    options.focusLossLimit = false;
  }

  if (!options.focusLossLimit) {
    options.focusLossLimitValue = RSHIELD_FOCUS_LOSS_LIMIT_MIN;
  }

  // A limit of 1 revokes access on the first focus loss, so there is nothing
  // left to resume.
  if (options.focusLossLimit && options.focusLossLimitValue === RSHIELD_FOCUS_LOSS_LIMIT_MIN) {
    options.resumeOnFullScreen = false;
  }

  return options;
}

/**
 * Pull RShield out of the rpddata payload.
 *
 * Accepts either the whole response (`{ data: { rShield, rShieldOptions } }`)
 * or the inner `data` object. Casing is tolerated because the API serializer
 * has emitted both `RShield` and `rShield` across builds.
 *
 * @returns {{enabled: boolean, options: object}|null}
 *          null when the payload carries no RShield information at all.
 */
export function parseRShieldPayload(payload) {
  try {
    if (!payload || typeof payload !== "object") return null;
    const src = payload.data && typeof payload.data === "object" ? payload.data : payload;

    const hasFlag =
      Object.prototype.hasOwnProperty.call(src, "rShield") ||
      Object.prototype.hasOwnProperty.call(src, "RShield") ||
      Object.prototype.hasOwnProperty.call(src, "isRShield");

    const rawOptions = src.rShieldOptions || src.RShieldOptions || null;

    // Nothing about RShield in this response -> let the caller fall back.
    if (!hasFlag && !rawOptions) return null;

    const enabled = toBool(
      src.rShield !== undefined
        ? src.rShield
        : src.RShield !== undefined
        ? src.RShield
        : src.isRShield,
      false
    );

    return {
      enabled,
      options: enabled ? normalizeRShieldOptions(rawOptions) : { ...RSHIELD_DEFAULT_OPTIONS },
    };
  } catch (e) {
    return null;
  }
}

/**
 * Translate normalized RShield options into DocumentProtection flags.
 *
 * Mapping:
 *   focusProtection      -> blurOnWindowBlur + blurOnHidden (the core "pause
 *                           viewing when the reader looks away" behavior)
 *   resumeOnFullScreen   -> the RPD is only readable in full screen. Enforced
 *                           from the moment the document opens and on every
 *                           full screen exit: the view blurs behind an overlay
 *                           carrying an "Enter Full Screen" button, and
 *                           viewing resumes once full screen is (re)entered
 *   focusLossLimit/Value -> allowed number of focus losses before access is
 *                           revoked for the session
 *   banReader            -> on a detected capture attempt, lock the RPD for
 *                           the rest of the session
 *   notifySender         -> emit an audit event the sender is notified from
 *
 * RShield does EXACTLY what the sender ticked and nothing more. Everything
 * below is off, because none of it is a sender-facing option and switching it
 * on silently changes how the RPD behaves for documents whose sender only
 * asked for, say, Notify Sender:
 *
 *   - right click / text selection / drag & drop guards - the RPD already
 *     applies its own restrictions
 *   - keyboard blocking of Ctrl+P / Ctrl+C / Ctrl+S - print in particular is
 *     governed by the document's own Print permission, so RShield must not
 *     intercept it. Screenshot keys are still watched: that is Capture
 *     Detection, which IS part of RShield (see captureDetection)
 *   - DevTools panel detection - no sender option, and the detection is a
 *     heuristic that can false positive, so it must never gate viewing
 *   - mandatory maximized window
 *   - the DocumentProtection watermark - the RPD renders its own watermark /
 *     steganography overlay, so this one stays off to avoid doubling up
 */
export function mapRShieldToProtectionFlags(options) {
  const o = normalizeRShieldOptions(options);

  return {
    // Focus Protection
    blurOnWindowBlur: o.focusProtection === true,
    blurOnHidden: o.focusProtection === true,

    // Capture Detection - watches screen capture shortcuts only. It never
    // blocks a keystroke; it feeds Ban Reader and Notify Sender.
    captureDetection: true,

    // Deliberately off - see the note above.
    disableRightClick: false,
    disableTextSelection: false,
    disableDragDrop: false,
    keyboardProtection: false,
    devToolsDetection: false,
    watermark: false,

    auditLogging: true,

    // RShield specific - consumed by useFocusLossLimit / useDocumentProtection
    focusProtection: o.focusProtection === true,
    resumeOnFullScreen: o.resumeOnFullScreen === true,
    focusLossLimit: o.focusLossLimit === true,
    focusLossLimitValue: o.focusLossLimitValue,
    banReader: o.banReader === true,
    notifySender: o.notifySender === true,
  };
}

/**
 * Resolve the effective RShield config for a document.
 *
 * @param {object} rShield  { enabled, options } from parseRShieldPayload
 * @param {boolean} killSwitchOn  AppContext master switch
 * @returns {object|null}   protection flags, or null when RShield is off
 */
export function resolveRShieldConfig(rShield, killSwitchOn = true) {
  if (killSwitchOn === false) return null;
  if (!rShield || rShield.enabled !== true) return null;
  return mapRShieldToProtectionFlags(rShield.options);
}

export default resolveRShieldConfig;
